import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { productAPI, orderAPI } from '../api';
import { getFileUrl } from '../api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ShoppingCart, Package, LogOut, History, Plus, Minus, Loader2, FileText, RotateCw, Star } from 'lucide-react';
import { InvoiceView } from './InvoiceView';

export const DealerDashboard = () => {
  const { user, logout } = useAuth();
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('products');
  const [cartOpen, setCartOpen] = useState(false);
  const [invoiceOrderId, setInvoiceOrderId] = useState(null);
  // My Orders — search filters (client-side)
  const [orderSearchProduct, setOrderSearchProduct] = useState('');
  const [orderSearchFrom, setOrderSearchFrom] = useState('');
  const [orderSearchTo, setOrderSearchTo] = useState('');

  // "My Top 5 Products" — aggregate total quantity per product_id across the
  // dealer's order history, take the top 5 that still exist in the current
  // catalog. Falls back gracefully when the dealer has no order history.
  const topProducts = React.useMemo(() => {
    if (!orders.length || !products.length) return [];
    const qtyByPid = {};
    orders.forEach((o) => {
      (o.items || []).forEach((it) => {
        if (!it.product_id) return;
        qtyByPid[it.product_id] = (qtyByPid[it.product_id] || 0) + Number(it.quantity || 0);
      });
    });
    const productMap = Object.fromEntries(products.map(p => [p.id, p]));
    return Object.entries(qtyByPid)
      .map(([pid, qty]) => ({ product: productMap[pid], totalQty: qty }))
      .filter(x => !!x.product)
      .sort((a, b) => b.totalQty - a.totalQty)
      .slice(0, 5);
  }, [orders, products]);

  useEffect(() => {
    fetchData();
  }, []);

  // Auto-refresh dealer dashboard when the user returns to the tab — keeps order
  // statuses and product list current without requiring a manual refresh.
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') fetchData();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  const fetchData = async () => {
    try {
      const [productsRes, ordersRes] = await Promise.all([
        productAPI.getAll(),
        orderAPI.getAll()
      ]);
      setProducts(productsRes.data);
      setOrders(ordersRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const addToCart = (product) => {
    const existing = cart.find(item => item.product_id === product.id);
    if (existing) {
      setCart(cart.map(item =>
        item.product_id === product.id
          ? { ...item, quantity: item.quantity + 1, total: (item.quantity + 1) * item.price }
          : item
      ));
    } else {
      setCart([...cart, {
        product_id: product.id,
        product_name: product.name,
        quantity: 1,
        price: product.price,
        total: product.price,
        unit: product.unit
      }]);
    }
    toast.success(`${product.name} added to cart`);
  };

  const updateCartQuantity = (productId, delta) => {
    setCart(cart.map(item => {
      if (item.product_id === productId) {
        const newQuantity = Math.max(1, item.quantity + delta);
        return { ...item, quantity: newQuantity, total: newQuantity * item.price };
      }
      return item;
    }));
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.product_id !== productId));
  };

  // Reorder: add every item from a past order back to the cart in one tap.
  // - Resolves each line to the *current* product master (fresh price / unit).
  // - Skips items whose product is no longer in the catalog OR is out of stock.
  // - Merges with existing cart quantities (adds on top).
  const reorderFromOrder = (order) => {
    const productMap = Object.fromEntries(products.map(p => [p.id, p]));
    const additions = [];
    const skipped = [];
    (order.items || []).forEach((it) => {
      const prod = productMap[it.product_id];
      if (!prod) { skipped.push(it.product_name + ' (unavailable)'); return; }
      if (prod.stock === 0) { skipped.push(prod.name + ' (out of stock)'); return; }
      additions.push({
        product_id: prod.id,
        product_name: prod.name,
        quantity: it.quantity,
        price: prod.price,
        unit: prod.unit,
      });
    });
    if (additions.length === 0) {
      toast.error('None of these items are currently available');
      return;
    }
    setCart((prev) => {
      const next = [...prev];
      additions.forEach((add) => {
        const idx = next.findIndex(c => c.product_id === add.product_id);
        if (idx >= 0) {
          const q = next[idx].quantity + add.quantity;
          next[idx] = { ...next[idx], quantity: q, total: q * next[idx].price };
        } else {
          next.push({ ...add, total: add.quantity * add.price });
        }
      });
      return next;
    });
    toast.success(
      `${additions.length} item${additions.length === 1 ? '' : 's'} added to cart` +
      (skipped.length ? ` · Skipped: ${skipped.join(', ')}` : '')
    );
    setCartOpen(true);
  };

  const placeOrder = async () => {
    if (cart.length === 0) {
      toast.error('Cart is empty');
      return;
    }

    try {
      await orderAPI.create({ items: cart });
      toast.success('Order placed successfully!');
      setCart([]);
      setCartOpen(false);
      fetchData();
      setActiveTab('orders');
    } catch (error) {
      toast.error('Failed to place order');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: { bg: '#E89D4F', text: 'white' },
      confirmed: { bg: '#2B5339', text: 'white' },
      dispatched: { bg: '#C05C35', text: 'white' },
      delivered: { bg: '#1a7a3a', text: 'white' }
    };
    const style = styles[status] || styles.pending;
    return (
      <Badge style={{ background: style.bg, color: style.text }} data-testid={`status-${status}`}>
        {status.toUpperCase()}
      </Badge>
    );
  };

  const cartTotal = cart.reduce((sum, item) => sum + item.total, 0);

  const fmtDate = (d) => d ? new Date(d.includes('T') ? d : d + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-';

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#2B5339' }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#FBF9F6' }}>
      {/* Header */}
      <header className="border-b" style={{ background: 'white', borderColor: '#E2DFD8' }}>
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 md:py-4 flex items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 md:gap-3">
              <img src="/logo.jpg" alt="Vikas Industries" className="w-12 h-12 md:w-32 md:h-auto rounded object-contain flex-shrink-0" />
              <h1 className="text-base md:text-2xl font-bold truncate" style={{ color: '#2B5339' }}>Vikas Industries</h1>
            </div>
            <p className="text-xs md:text-sm truncate" style={{ color: '#5C6B61' }}>Welcome, {user?.name}</p>
          </div>
          <div className="flex items-center gap-2 md:gap-4 flex-shrink-0">
            <Dialog open={cartOpen} onOpenChange={setCartOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="relative" data-testid="cart-btn">
                  <ShoppingCart className="w-5 h-5" />
                  {cart.length > 0 && (
                    <span className="absolute -top-2 -right-2 w-5 h-5 rounded-full text-xs flex items-center justify-center" style={{ background: '#C05C35', color: 'white' }}>
                      {cart.length}
                    </span>
                  )}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl" data-testid="cart-dialog">
                <DialogHeader>
                  <DialogTitle>Your Cart</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  {cart.length === 0 ? (
                    <p className="text-center py-8" style={{ color: '#5C6B61' }}>Cart is empty</p>
                  ) : (
                    <>
                      {cart.map((item) => (
                        <div key={item.product_id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 border rounded" style={{ borderColor: '#E2DFD8' }} data-testid="cart-item">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium">{item.product_name}</h4>
                            <p className="text-sm" style={{ color: '#5C6B61' }}>₹{item.price} per {item.unit}</p>
                          </div>
                          <div className="flex items-center gap-2 md:gap-3 flex-wrap">
                            <Button size="sm" variant="outline" onClick={() => updateCartQuantity(item.product_id, -1)} data-testid="decrease-qty-btn">
                              <Minus className="w-4 h-4" />
                            </Button>
                            <span className="w-10 md:w-12 text-center" data-testid="cart-item-quantity">{item.quantity}</span>
                            <Button size="sm" variant="outline" onClick={() => updateCartQuantity(item.product_id, 1)} data-testid="increase-qty-btn">
                              <Plus className="w-4 h-4" />
                            </Button>
                            <span className="w-20 md:w-24 text-right font-medium" data-testid="cart-item-total">₹{item.total.toFixed(2)}</span>
                            <Button size="sm" variant="destructive" onClick={() => removeFromCart(item.product_id)} data-testid="remove-cart-item-btn">
                              Remove
                            </Button>
                          </div>
                        </div>
                      ))}
                      <div className="border-t pt-4" style={{ borderColor: '#E2DFD8' }}>
                        <div className="flex justify-between items-center mb-4">
                          <span className="text-lg font-bold">Total:</span>
                          <span className="text-2xl font-bold" style={{ color: '#2B5339' }} data-testid="cart-total">₹{cartTotal.toFixed(2)}</span>
                        </div>
                        <Button onClick={placeOrder} className="w-full" style={{ background: '#2B5339' }} data-testid="place-order-btn">
                          Place Order
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </DialogContent>
            </Dialog>
            <Button variant="outline" onClick={logout} data-testid="logout-btn">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="border-b" style={{ background: 'white', borderColor: '#E2DFD8' }}>
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex gap-4 md:gap-8 overflow-x-auto">
            <button
              onClick={() => setActiveTab('products')}
              className={`py-4 px-2 border-b-2 font-medium transition-colors ${activeTab === 'products' ? '' : 'border-transparent'}`}
              style={activeTab === 'products' ? { borderColor: '#2B5339', color: '#2B5339' } : { color: '#5C6B61' }}
              data-testid="products-tab"
            >
              <Package className="w-5 h-5 inline mr-2" />
              Products
            </button>
            <button
              onClick={() => setActiveTab('orders')}
              className={`py-4 px-2 border-b-2 font-medium transition-colors ${activeTab === 'orders' ? '' : 'border-transparent'}`}
              style={activeTab === 'orders' ? { borderColor: '#2B5339', color: '#2B5339' } : { color: '#5C6B61' }}
              data-testid="orders-tab"
            >
              <History className="w-5 h-5 inline mr-2" />
              My Orders
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8">
        {activeTab === 'products' && (
          <div data-testid="products-section">
            {topProducts.length > 0 && (
              <div className="mb-6" data-testid="dealer-top-products">
                <h3 className="text-base font-bold mb-2 flex items-center gap-1.5" style={{ color: '#2B5339' }}>
                  <Star className="w-4 h-4" fill="#C05C35" strokeWidth={0} />
                  My Top 5 Products
                  <span className="text-xs font-normal" style={{ color: '#5C6B61' }}>· based on your order history</span>
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {topProducts.map(({ product, totalQty }) => (
                    <Card key={product.id} className="p-3 bg-white border hover:shadow-sm transition-all flex flex-col relative" style={{ borderColor: '#C05C35' }} data-testid="top-product-card">
                      <Badge className="absolute top-1 right-1 text-[9px] px-1.5 py-0 font-bold" style={{ background: '#C05C35', color: 'white' }}>
                        {totalQty} ordered
                      </Badge>
                      {product.image_url && (
                        <img src={product.image_url.startsWith('http') ? product.image_url : getFileUrl(product.image_url)} alt={product.name} className="w-full h-24 object-cover rounded mb-2" />
                      )}
                      <h3 className="text-sm font-bold mb-1 leading-tight" style={{ color: '#1E2420' }}>{product.name}</h3>
                      <div className="flex justify-between items-center mb-2 mt-auto">
                        <div>
                          <p className="text-base font-bold leading-tight" style={{ color: '#2B5339' }}>₹{product.price}</p>
                          <p className="text-[10px]" style={{ color: '#5C6B61' }}>per {product.unit}</p>
                        </div>
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0.5">Stk: {product.stock}</Badge>
                      </div>
                      <Button
                        onClick={() => addToCart(product)}
                        className="w-full h-8 text-xs"
                        style={{ background: '#2B5339' }}
                        disabled={product.stock === 0}
                        data-testid="top-add-to-cart-btn"
                      >
                        <ShoppingCart className="w-3 h-3 mr-1" />
                        Add
                      </Button>
                    </Card>
                  ))}
                </div>
              </div>
            )}
            <h2 className="text-xl font-bold mb-4" style={{ color: '#2B5339' }}>Available Products</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {products.map((product) => (
                <Card key={product.id} className="p-3 bg-white border hover:shadow-sm transition-all flex flex-col" style={{ borderColor: '#E2DFD8' }} data-testid="product-card">
                  {product.image_url && (
                    <img src={product.image_url.startsWith('http') ? product.image_url : getFileUrl(product.image_url)} alt={product.name} className="w-full h-24 object-cover rounded mb-2" />
                  )}
                  <h3 className="text-sm font-bold mb-1 leading-tight" style={{ color: '#1E2420' }}>{product.name}</h3>
                  {product.description && (
                    <p className="text-xs mb-2 line-clamp-2" style={{ color: '#5C6B61' }}>{product.description}</p>
                  )}
                  <div className="flex justify-between items-center mb-2 mt-auto">
                    <div>
                      <p className="text-base font-bold leading-tight" style={{ color: '#2B5339' }}>₹{product.price}</p>
                      <p className="text-[10px]" style={{ color: '#5C6B61' }}>per {product.unit}</p>
                    </div>
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0.5" data-testid="product-stock">Stk: {product.stock}</Badge>
                  </div>
                  <Button
                    onClick={() => addToCart(product)}
                    className="w-full h-8 text-xs"
                    style={{ background: '#2B5339' }}
                    disabled={product.stock === 0}
                    data-testid="add-to-cart-btn"
                  >
                    <ShoppingCart className="w-3 h-3 mr-1" />
                    Add
                  </Button>
                </Card>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'orders' && (
          <div data-testid="orders-section">
            {invoiceOrderId ? (
              <InvoiceView orderId={invoiceOrderId} onBack={() => setInvoiceOrderId(null)} />
            ) : (
              <>
                <h2 className="text-2xl font-bold mb-4" style={{ color: '#2B5339' }}>My Orders</h2>
            {orders.length === 0 ? (
              <Card className="p-12 text-center" style={{ borderColor: '#E2DFD8' }}>
                <History className="w-16 h-16 mx-auto mb-4" style={{ color: '#5C6B61' }} />
                <p style={{ color: '#5C6B61' }}>No orders yet</p>
              </Card>
            ) : (
              <>
              {/* Search filters — product name + date range (client-side filter on `orders`) */}
              <Card className="p-3 mb-4 bg-white border" style={{ borderColor: '#E2DFD8' }} data-testid="my-orders-filters">
                <div className="flex flex-wrap items-end gap-3">
                  <div className="flex-1 min-w-[160px]">
                    <label className="text-xs font-medium block mb-1" style={{ color: '#5C6B61' }}>Search Product</label>
                    <div className="relative">
                      <Input
                        type="text"
                        value={orderSearchProduct}
                        onChange={(e) => setOrderSearchProduct(e.target.value)}
                        placeholder="e.g. Bypass"
                        className="w-full h-9 pr-7"
                        data-testid="my-orders-search-product"
                      />
                      {orderSearchProduct && (
                        <button
                          type="button"
                          onClick={() => setOrderSearchProduct('')}
                          className="absolute top-1/2 right-2 -translate-y-1/2 w-4 h-4 rounded-full flex items-center justify-center text-[11px] font-bold leading-none"
                          style={{ color: '#C05C35', background: '#FBF9F6', border: '1px solid #E2DFD8' }}
                          aria-label="Clear product search"
                          data-testid="my-orders-search-product-clear"
                        >×</button>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium block mb-1" style={{ color: '#5C6B61' }}>From</label>
                    <Input type="date" value={orderSearchFrom} onChange={(e) => setOrderSearchFrom(e.target.value)} className="h-9 w-36" data-testid="my-orders-search-from" />
                  </div>
                  <div>
                    <label className="text-xs font-medium block mb-1" style={{ color: '#5C6B61' }}>To</label>
                    <Input type="date" value={orderSearchTo} onChange={(e) => setOrderSearchTo(e.target.value)} className="h-9 w-36" data-testid="my-orders-search-to" />
                  </div>
                  {(orderSearchProduct || orderSearchFrom || orderSearchTo) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setOrderSearchProduct(''); setOrderSearchFrom(''); setOrderSearchTo(''); }}
                      className="h-9"
                      data-testid="my-orders-clear-filters"
                    >Clear</Button>
                  )}
                </div>
              </Card>

              {(() => {
                const q = orderSearchProduct.trim().toLowerCase();
                const fromTs = orderSearchFrom ? new Date(orderSearchFrom + 'T00:00:00').getTime() : null;
                const toTs = orderSearchTo ? new Date(orderSearchTo + 'T23:59:59.999').getTime() : null;
                const filtered = orders.filter((o) => {
                  if (q && !(o.items || []).some(i => (i.product_name || '').toLowerCase().includes(q))) return false;
                  if (fromTs || toTs) {
                    const t = new Date(o.created_at).getTime();
                    if (fromTs && t < fromTs) return false;
                    if (toTs && t > toTs) return false;
                  }
                  return true;
                });
                if (filtered.length === 0) {
                  return (
                    <Card className="p-8 text-center" style={{ borderColor: '#E2DFD8' }} data-testid="my-orders-empty-filtered">
                      <p className="text-sm" style={{ color: '#5C6B61' }}>No orders match your search.</p>
                    </Card>
                  );
                }
                return (
                  <div className="space-y-4">
                    {filtered.map((order) => (
                  <Card key={order.id} className="p-6 bg-white border" style={{ borderColor: '#E2DFD8' }} data-testid="order-card">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="font-bold text-lg" style={{ color: '#1E2420' }}>Order #{order.id.slice(0, 8)}</h3>
                        <p className="text-sm" style={{ color: '#5C6B61' }}>
                          {new Date(order.created_at).toLocaleDateString('en-IN', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                      {getStatusBadge(order.status)}
                    </div>
                    
                    <div className="mb-4">
                      {order.items.map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm py-1">
                          <span style={{ color: '#5C6B61' }}>{item.product_name} x {item.quantity}</span>
                          <span style={{ color: '#1E2420' }}>₹{item.total}</span>
                        </div>
                      ))}
                    </div>

                    <div className="border-t pt-4 mb-4" style={{ borderColor: '#E2DFD8' }}>
                      <div className="flex justify-between font-bold">
                        <span>Total Amount:</span>
                        <span style={{ color: '#2B5339' }} data-testid="order-total">₹{order.total_amount}</span>
                      </div>
                    </div>

                    {order.status === 'confirmed' && order.delivery_date && (
                      <div className="bg-[#FBF9F6] p-4 rounded" data-testid="delivery-info">
                        <p className="text-sm font-medium mb-1" style={{ color: '#2B5339' }}>Confirmed Delivery</p>
                        <p className="text-sm" style={{ color: '#5C6B61' }}>
                          {fmtDate(order.delivery_date)} at {order.delivery_time}
                        </p>
                      </div>
                    )}

                    {order.status === 'dispatched' && (
                      <div className="bg-[#FBF9F6] p-4 rounded mb-3" data-testid="dispatch-info">
                        <p className="text-sm font-medium mb-2" style={{ color: '#C05C35' }}>Order Dispatched</p>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p style={{ color: '#5C6B61' }}>Vehicle Number</p>
                            <p className="font-medium" style={{ color: '#1E2420' }} data-testid="vehicle-number">{order.vehicle_number}</p>
                          </div>
                          <div>
                            <p style={{ color: '#5C6B61' }}>Driver Mobile</p>
                            <p className="font-medium" style={{ color: '#1E2420' }} data-testid="driver-mobile">{order.driver_mobile}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {order.status === 'delivered' && (
                      <div className="bg-[#FBF9F6] p-4 rounded mb-3" data-testid="delivered-info">
                        <p className="text-sm font-medium mb-2" style={{ color: '#1a7a3a' }}>Order Delivered Successfully</p>
                        {order.vehicle_number && (
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <p style={{ color: '#5C6B61' }}>Vehicle Number</p>
                              <p className="font-medium" style={{ color: '#1E2420' }}>{order.vehicle_number}</p>
                            </div>
                            <div>
                              <p style={{ color: '#5C6B61' }}>Driver Mobile</p>
                              <p className="font-medium" style={{ color: '#1E2420' }}>{order.driver_mobile}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="flex gap-2 mt-2 flex-wrap">
                      {(order.status === 'dispatched' || order.status === 'delivered') && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setInvoiceOrderId(order.id)}
                          data-testid="dealer-view-invoice-btn"
                        >
                          <FileText className="w-4 h-4 mr-2" /> View Invoice
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => reorderFromOrder(order)}
                        style={{ color: '#2B5339', borderColor: '#2B5339' }}
                        data-testid="dealer-reorder-btn"
                      >
                        <RotateCw className="w-4 h-4 mr-2" /> Reorder
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
                );
              })()}
              </>
            )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};